import { createContext, useContext, useState, useEffect } from 'react';
import { translations, getTranslation, getSectionTranslations } from '@/lib/translations';
import { api } from '@/lib/api';
import { storage } from '@/lib/storage';
import i18n from '@/i18n';

const LanguageContext = createContext();

const LANGUAGE_KEY = 'language';
const LANGUAGE_SELECTED_KEY = 'language_selected';

// Helper function to get initial language from localStorage
const getInitialLanguage = () => {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem(LANGUAGE_KEY);
    if (saved === 'pt' || saved === 'en') {
      return saved;
    }
  }
  return null; // Return null to indicate no language selected yet
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    const saved = getInitialLanguage();
    // Set initial HTML lang attribute
    if (typeof document !== 'undefined') {
      document.documentElement.lang = saved === 'en' ? 'en' : 'pt-BR';
    }
    return saved || 'pt'; // Default to Portuguese, but modal will show if not selected
  });
  
  const [hasSelectedLanguage, setHasSelectedLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(LANGUAGE_SELECTED_KEY) === 'true' || 
             localStorage.getItem(LANGUAGE_KEY) !== null;
    }
    return false;
  });

  // Save language to localStorage when it changes and sync with i18n
  useEffect(() => {
    if (language) {
      localStorage.setItem(LANGUAGE_KEY, language);
      
      // Update HTML lang attribute
      document.documentElement.lang = language === 'pt' ? 'pt-BR' : 'en';
      
      // Sync with react-i18next
      const i18nLang = language === 'pt' ? 'pt-BR' : 'en-US';
      if (i18n.language !== i18nLang) {
        i18n.changeLanguage(i18nLang);
      }
      
      // Dispatch event for other components to react
      window.dispatchEvent(new CustomEvent('languageChange', { detail: { language } }));
    }
  }, [language]);
  
  // Listen for i18n language changes to sync back
  useEffect(() => {
    const handleI18nChange = (newLang) => {
      const contextLang = newLang.startsWith('pt') ? 'pt' : 'en';
      if (language !== contextLang) {
        setLanguageState(contextLang);
      }
    };
    
    i18n.on('languageChanged', handleI18nChange);
    return () => {
      i18n.off('languageChanged', handleI18nChange);
    };
  }, [language]);

  // Sync language with backend on mount if user is logged in
  useEffect(() => {
    const syncLanguageFromBackend = async () => {
      const user = storage.getUser();
      const token = storage.getToken();
      
      if (user && token && !hasSelectedLanguage) {
        try {
          const response = await api.get('/me');
          const preferredLang = response.data.preferredLanguage;
          if (preferredLang && (preferredLang === 'pt' || preferredLang === 'en')) {
            localStorage.setItem(LANGUAGE_KEY, preferredLang);
            localStorage.setItem(LANGUAGE_SELECTED_KEY, 'true');
            setLanguageState(preferredLang);
            setHasSelectedLanguage(true);
          }
        } catch (error) {
          // Could not sync language from backend - using local preference
        }
      }
    };
    
    syncLanguageFromBackend();
  }, []);

  // Set language with persistence (local + backend if authenticated)
  const setLanguage = async (lang) => {
    if (lang === 'pt' || lang === 'en') {
      localStorage.setItem(LANGUAGE_KEY, lang);
      localStorage.setItem(LANGUAGE_SELECTED_KEY, 'true');
      setLanguageState(lang);
      setHasSelectedLanguage(true);
      
      // If user is authenticated, also save to backend
      const user = storage.getUser();
      const token = storage.getToken();
      
      if (user && token) {
        try {
          await api.patch('/me/language', { preferredLanguage: lang });
          // Language preference saved to backend
        } catch (error) {
          // Could not save language to backend - using local preference only
        }
      }
    }
  };

  // Translation function using centralized translations
  // Usage: t('section.key') e.g., t('navbar.login') or t('common.save')
  const t = (key) => {
    const keys = key.split('.');
    
    // Map old section names to new ones
    const sectionMap = {
      'nav': 'navbar',
      'dashboard': 'host'
    };
    
    if (keys.length >= 2) {
      const section = keys[0];
      const translationKey = keys.slice(1).join('.');
      
      // Apply section mapping
      const mappedSection = sectionMap[section] || section;
      const value = getTranslation(mappedSection, translationKey, language);
      
      // Only return if we found a valid translation
      if (value !== translationKey) {
        return value;
      }
    }
    
    // Return the key as fallback
    return key;
  };

  // Get all translations for a section
  // Usage: const navTexts = getSection('navbar')
  const getSection = (section) => {
    return getSectionTranslations(section, language);
  };

  const toggleLanguage = () => {
    const newLang = language === 'pt' ? 'en' : 'pt';
    setLanguage(newLang);
  };

  // Check if language modal should be shown
  const shouldShowLanguageModal = !hasSelectedLanguage;

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      toggleLanguage, 
      t,
      getSection,
      hasSelectedLanguage,
      shouldShowLanguageModal
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    // Return default values instead of throwing error (compatibility mode)
    console.warn('useLanguage called outside LanguageProvider - using defaults');
    return {
      language: 'en',
      setLanguage: () => {},
      toggleLanguage: () => {},
      t: (key) => key,
      getSection: () => ({}),
      hasSelectedLanguage: true,
      shouldShowLanguageModal: false
    };
  }
  return context;
};

export default LanguageContext;
